import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import {NavigationService, ProblemInstanceService, TaskUpdateService, TaskView} from "planner-shared";
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    Validators
} from "@angular/forms";
import {NotificationService, TimeService} from "@shared";
import {AdminTaskUpdate} from "../../../../shared/models/admin-task-update.model";
import {TaskEditService} from "../../../../shared/services/task-edit/task-edit.service";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {AdminTaskUpdateResult} from "../../../../shared/models/admin-task-update-result.model";

@UntilDestroy()
@Component({
    selector: 'app-employee-assigned-task-edit',
    templateUrl: './employee-assigned-task-edit.component.html',
    styleUrls: ['./employee-assigned-task-edit.component.scss'],
    standalone: false
})
export class EmployeeAssignedTaskEditComponent implements OnInit {
    private readonly formBuilder = inject(FormBuilder);
    private readonly timeService = inject(TimeService);
    private readonly navigationService = inject(NavigationService);
    private readonly taskUpdateService = inject(TaskUpdateService);
    private readonly notificationService = inject(NotificationService);
    private readonly taskEditService = inject(TaskEditService);
    private readonly problemInstanceService = inject(ProblemInstanceService);


    @Input() public employeeTask: TaskView;

    @Input() public taskAddressPresent: boolean;

    @Output() public updateProblemInstanceEmitter: EventEmitter<AdminTaskUpdateResult> = new EventEmitter();
    @Output() public disableDraggingEmitter: EventEmitter<boolean> = new EventEmitter();
    public saving: boolean;
    public taskForm: FormGroup;
    public rosterPresent: boolean;

    ngOnInit(): void {
        this.taskForm = this.initTaskForm();

        this.problemInstanceService.rosterDepartmentIsPartOfInstance().subscribe(rosterPresent => {
            this.rosterPresent = rosterPresent;
        });
    }

    public getTaskDurationError(taskForm: FormGroup): string {
        return this.taskEditService.getTaskDurationError(taskForm);
    }

    public save(event?: Event): void {
        if (event) { event.stopPropagation(); }
        if (!this.taskForm || this.taskForm.pristine || this.taskForm.invalid || this.saving ||
            (this.taskForm.value.active === this.employeeTask.isActive &&
            this.taskForm.value.duration.toString() === this.employeeTask.duration.toString()) )
        {
            this.updateProblemInstanceEmitter.emit(null);
            this.employeeTask.editing = false;
            return;
        }
        this.saving = true;
        this.navigationService.disableNextButton(true);

        const adminTaskUpdate: AdminTaskUpdate[] =
            [new AdminTaskUpdate(this.employeeTask.taskId, this.taskForm.value.active, this.taskForm.value.duration)];

        this.taskUpdateService.updateAdministrativeTasks(adminTaskUpdate)
            .pipe(untilDestroyed(this))
            .subscribe(requestResult => {
                this.updateProblemInstanceEmitter.emit(requestResult);
                this.navigationService.disableNextButton(false);
                this.notificationService.success('TASK_UPDATED', null, {id: this.employeeTask.serviceName});
                this.employeeTask.editing = false;
            });
    }

    public revert(): void {
        if (this.taskForm.dirty) {
            this.taskForm.patchValue(this.employeeTask);
            this.taskForm.markAsPristine();
            this.navigationService.disableNextButton(false);
            this.disableDraggingEmitter.emit(false);
            this.employeeTask.editing = false;
            this.updateProblemInstanceEmitter.emit(null);
        }
    }

    public clickedOutside(): void {
        this.updateProblemInstanceEmitter.emit(null);
        if (this.taskForm.dirty) {
            this.save();
        } else {
            this.employeeTask.editing = false;
        }
        this.disableDraggingEmitter.emit(false);
        this.navigationService.disableNextButton(false);
    }

    private initTaskForm(): FormGroup {
        return this.formBuilder.group(
            {
                duration: [
                    this.employeeTask.duration,
                    Validators.compose([
                        Validators.min(1),
                        this.validateDuration
                    ])
                ],
                active: [
                    this.employeeTask.isActive
                ]
            }
        );
    }

    private validateDuration = (control: AbstractControl) => {
        const difference = this.timeService.timeDifference(this.employeeTask.fromTime, this.employeeTask.toTime);

        if (control.value > difference) {
            return {
                durationTooLong: 'durationTooLong'
            };
        }
        return {};
    };

}
